import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Database, Info, Loader2, PlugZap, Save } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import type { ConexaoPec, ResultadoTestePec } from '@/features/pec/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import { cn, paraBooleano } from '@/lib/utils';

const esquema = z.object({
  host: z.string().min(1, 'Informe o endereço do servidor.'),
  porta: z.coerce.number().int().min(1).max(65535),
  database: z.string().min(1, 'Informe o nome do banco.'),
  usuario: z.string().min(1, 'Informe o usuário de leitura.'),
  senha: z.string().optional(),
  sslMode: z.enum(['disable', 'require']),
  schemaName: z.string().min(1),
  ativo: z.boolean(),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaPec() {
  const queryClient = useQueryClient();
  const [resultadoTeste, setResultadoTeste] = React.useState<ResultadoTestePec | null>(null);

  const { data: conexao, isPending } = useQuery({
    queryKey: ['pec-conexao'],
    queryFn: () => api.get<ConexaoPec>('/pec/conexao'),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    values: {
      host: conexao?.host ?? '',
      porta: conexao?.porta ?? 5432,
      database: conexao?.database ?? '',
      usuario: conexao?.usuario ?? '',
      senha: '',
      sslMode: (conexao?.sslMode as 'disable' | 'require') ?? 'disable',
      schemaName: conexao?.schemaName ?? 'public',
      ativo: conexao?.ativo ?? false,
    },
  });

  const testar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<ResultadoTestePec>('/pec/conexao/testar', {
        host: dados.host,
        porta: dados.porta,
        database: dados.database,
        usuario: dados.usuario,
        // Senha em branco usa a já gravada.
        senha: dados.senha || undefined,
        sslMode: dados.sslMode,
        schemaName: dados.schemaName,
      }),
    onSuccess: async (resultado) => {
      setResultadoTeste(resultado);
      await queryClient.invalidateQueries({ queryKey: ['pec-conexao'] });
    },
    onError: (erro) => {
      setResultadoTeste({
        ok: false,
        mensagem: erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao testar a conexão.',
      });
    },
  });

  const salvar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.patch<ConexaoPec>('/pec/conexao', {
        ...dados,
        senha: dados.senha || undefined,
      }),
    onSuccess: async () => {
      toast.success('Configuração salva.');
      formulario.setValue('senha', '');
      await queryClient.invalidateQueries({ queryKey: ['pec-conexao'] });
      await queryClient.invalidateQueries({ queryKey: ['pec-situacao'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoSalvar = formulario.handleSubmit((dados) => salvar.mutate(dados));
  const aoTestar = formulario.handleSubmit((dados) => testar.mutate(dados));

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-72" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const ativo = formulario.watch('ativo');

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Integração com o PEC"
        descricao="Conexão somente-leitura ao banco do Prontuário Eletrônico do Cidadão, usada para pré-preencher os dados do paciente."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">O KAVI nunca escreve no banco do PEC.</p>
          <p className="mt-0.5">
            Use um usuário de banco com permissão apenas de <code>SELECT</code>. O telefone do
            paciente é deliberadamente ignorado nesta consulta — o operador da unidade sempre digita
            o contato, confirmando-o com o paciente.
          </p>
        </div>
      </div>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Database className="size-4" aria-hidden="true" />
            Dados da conexão
          </CartaoTitulo>
          <CartaoDescricao>
            {conexao?.ultimoTesteEm ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Último teste em {formatarDataHora(conexao.ultimoTesteEm)}
                <Etiqueta tom={conexao.ultimoTesteOk ? 'sucesso' : 'perigo'}>
                  {conexao.ultimoTesteOk ? 'compatível' : 'com problema'}
                </Etiqueta>
              </span>
            ) : (
              'A conexão ainda não foi testada.'
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <form
            onSubmit={(evento) => void aoSalvar(evento)}
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
              <Campo
                id="pec-host"
                rotulo="Servidor"
                obrigatorio
                ajuda="IP ou nome de rede do servidor do PEC."
                erro={formulario.formState.errors.host?.message}
              >
                <Entrada placeholder="10.0.0.20" {...formulario.register('host')} />
              </Campo>

              <Campo
                id="pec-porta"
                rotulo="Porta"
                obrigatorio
                erro={formulario.formState.errors.porta?.message}
              >
                <Entrada type="number" inputMode="numeric" {...formulario.register('porta')} />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="pec-database"
                rotulo="Banco de dados"
                obrigatorio
                erro={formulario.formState.errors.database?.message}
              >
                <Entrada placeholder="esus" {...formulario.register('database')} />
              </Campo>

              <Campo
                id="pec-schema"
                rotulo="Schema"
                obrigatorio
                erro={formulario.formState.errors.schemaName?.message}
              >
                <Entrada placeholder="public" {...formulario.register('schemaName')} />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="pec-usuario"
                rotulo="Usuário"
                obrigatorio
                ajuda="Precisa apenas de SELECT nas tabelas de cadastro."
                erro={formulario.formState.errors.usuario?.message}
              >
                <Entrada autoComplete="off" {...formulario.register('usuario')} />
              </Campo>

              <Campo
                id="pec-senha"
                rotulo="Senha"
                ajuda={
                  conexao?.temSenha
                    ? 'Já existe uma senha salva. Deixe em branco para mantê-la.'
                    : 'Nenhuma senha salva ainda.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  placeholder={conexao?.temSenha ? '••••••••' : ''}
                  {...formulario.register('senha')}
                />
              </Campo>
            </div>

            <Campo
              id="pec-ssl"
              rotulo="SSL"
              ajuda="Use 'require' quando o servidor do PEC estiver fora da rede interna."
            >
              <Selecao {...formulario.register('sslMode')}>
                <option value="disable">Desabilitado</option>
                <option value="require">Obrigatório</option>
              </Selecao>
            </Campo>

            <Campo
              id="pec-ativo"
              rotulo="Situação da integração"
              ajuda="Desativada, o botão de busca não aparece no formulário da UBS."
            >
              <Selecao
                {...formulario.register('ativo', {
                  setValueAs: paraBooleano,
                })}
              >
                <option value="false">Desativada</option>
                <option value="true">Ativa</option>
              </Selecao>
            </Campo>

            {ativo && !conexao?.ultimoTesteOk && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Teste a conexão antes de ativar: sem isso, o botão de busca aparece para a UBS e
                falha a cada clique.
              </p>
            )}

            {resultadoTeste && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-md border p-3 text-sm',
                  resultadoTeste.ok && resultadoTeste.compativel
                    ? 'border-status-agendado-borda bg-status-agendado-fundo text-status-agendado-texto'
                    : 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto',
                )}
              >
                {resultadoTeste.ok && resultadoTeste.compativel ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">{resultadoTeste.mensagem}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => void aoTestar()}
                disabled={testar.isPending}
              >
                {testar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <PlugZap aria-hidden="true" />
                )}
                {testar.isPending ? 'Testando…' : 'Testar conexão'}
              </Botao>

              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>
            </div>
          </form>
        </CartaoConteudo>
      </Cartao>

      <Cartao className="mt-5">
        <CartaoCabecalho>
          <CartaoTitulo>Permissões necessárias no banco do PEC</CartaoTitulo>
          <CartaoDescricao>
            Encaminhe ao responsável pelo servidor do PEC, substituindo o nome do usuário.
          </CartaoDescricao>
        </CartaoCabecalho>
        <CartaoConteudo>
          <pre className="bg-silenciado overflow-x-auto rounded-md border p-3 text-xs">
            {`CREATE USER kavi_leitura WITH PASSWORD 'defina-uma-senha';
GRANT CONNECT ON DATABASE ${conexao?.database || 'esus'} TO kavi_leitura;
GRANT USAGE ON SCHEMA ${conexao?.schemaName || 'public'} TO kavi_leitura;
GRANT SELECT ON ALL TABLES IN SCHEMA ${conexao?.schemaName || 'public'} TO kavi_leitura;`}
          </pre>
        </CartaoConteudo>
      </Cartao>
    </div>
  );
}
